/**
 * UserMenu — authenticated user avatar with dropdown menu.
 *
 * Shows the user's avatar (initials fallback) and a dropdown
 * containing: My Profile, Creator Dashboard (when applicable),
 * Settings, Notifications, and Logout.
 *
 * Uses the existing Dropdown UI primitive. Hidden for guests.
 */

import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/auth';
import { Avatar, Dropdown } from '@/components/ui';
import type { DropdownItem } from '@/components/ui/Dropdown/Dropdown';
import { ROUTES } from '@/app/routes/paths';

export function UserMenu(): JSX.Element | null {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  const isAdmin = user.roles.includes('admin');

  const items: DropdownItem[] = [
    {
      label: 'My Profile',
      onClick: () => navigate(ROUTES.WORKSPACE_PROFILE),
    },
    {
      label: 'Creator Dashboard',
      onClick: () => navigate(ROUTES.DASHBOARD),
    },
    ...(isAdmin
      ? [{
          label: 'Review Workspace',
          onClick: () => navigate(ROUTES.REVIEW),
        }]
      : []),
    {
      label: 'Settings',
      onClick: () => navigate(ROUTES.WORKSPACE_ACCOUNT),
    },
    {
      label: 'Notifications',
      onClick: () => navigate(ROUTES.WORKSPACE),
    },
    {
      label: 'Logout',
      onClick: () => void signOut(),
    },
  ];

  return (
    <Dropdown
      align="right"
      items={items}
      trigger={
        <div className="flex items-center gap-2 rounded-md p-1 transition-colors hover:bg-neutral-100">
          <Avatar
            name={user.fullName || user.email}
            size="sm"
            alt={user.fullName || user.email}
          />
          <span className="hidden text-sm font-medium text-foreground lg:inline">
            {user.fullName?.split(' ')[0] ?? user.email.split('@')[0]}
          </span>
        </div>
      }
    />
  );
}
